import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as MoveBackward } from '../../../assets/icons/device-management/move-backward.svg';
import { ReactComponent as MoveForward } from '../../../assets/icons/device-management/move-forward.svg';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  fetchChargingStationList,
  setSelectedStation,
} from '../../../redux/slice/StationManagementSidePanelSlice';
import SelectField from '../../../shared-components/select-field/SelectField';

/**
 * DeviceSearch
 * @returns
 */
function DeviceSearch() {
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id: idInQuery } = useParams<{ id: string }>();
  const { page } = useParams<{ page: string }>();
  const location = useLocation();
  const moduleName = location.pathname.split('/')[2];
  const { chargingStationList, selectedChargingPoint } = useAppSelector((s) => s.stationManagement);

  // Convert redux list to dropdown options
  const dropdownData = React.useMemo(() => {
    const formatted = chargingStationList.map((station: any) => ({
      label: station.name,
      value: station._id,
    }));

    return formatted.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  }, [chargingStationList]);

  /**
   * Set selected station in Redux and navigate
   * @param id
   * @returns
   */
  const handleSelectStation = (value: string | { label: string; value: string }) => {
    const station = chargingStationList.find((s) => s._id === value);
    if (!station) return;
    if (!station._id) return;
    if (selectedChargingPoint?.id === station._id) return;

    dispatch(
      setSelectedStation({
        id: station._id,
        name: station.name,
        chargingPointSn: station._id,
      }),
    );

    // Debug log
    if (moduleName === 'station-management') {
      navigate({
        pathname: `/ev-admin/${moduleName}/${page}/station/${station._id}/station-dashboard`,
        search: `?searchBy=${station.name}`,
      });
    } else {
      navigate({
        pathname: `/ev-admin/${moduleName}/station/${station._id}/station-dashboard`,
        search: `?searchBy=${station.name}`,
      });
    }
  };

  /**
   * Get next or previous station for navigation
   * @param direction
   * @returns
   */
  const handleNextOrPrevious = (direction: 'next' | 'prev') => {
    if (!selectedChargingPoint) return;

    const currentIndex = dropdownData.findIndex((s) => s.value === selectedChargingPoint.id);

    if (currentIndex === -1) return;

    let targetIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;

    if (targetIndex < 0) targetIndex = 0;
    if (targetIndex >= dropdownData.length) targetIndex = dropdownData.length - 1;

    const nextOption = dropdownData[targetIndex];

    if (nextOption) handleSelectStation(nextOption.value);
  };

  // Initial fetch of stations
  useEffect(() => {
    dispatch(fetchChargingStationList());
  }, []);

  // Update selection if query param exists
  useEffect(() => {
    if (!selectedChargingPoint?.id && idInQuery && chargingStationList.length > 0) {
      handleSelectStation(idInQuery);
    }
  }, [chargingStationList, idInQuery]);

  return (
    <div className="device-search-wrap">
      <div className="device-search-container" dir={i18n.dir()}>
        <span
          role="presentation"
          onClick={() => handleNextOrPrevious('prev')}
          className={i18n.dir() === 'rtl' ? 'rotate-180' : ''}>
          <MoveBackward />
        </span>

        <div className="SelectField">
          <SelectField
            name="chargingStation"
            selectedValue={selectedChargingPoint?.id || ''}
            onChangeValue={(_, value) => handleSelectStation(value)}
            options={dropdownData}
            searchLabel
          />
        </div>
        <span
          role="presentation"
          onClick={() => handleNextOrPrevious('next')}
          className={i18n.dir() === 'rtl' ? 'rotate-180' : ''}>
          <MoveForward />
        </span>
      </div>
    </div>
  );
}

export default DeviceSearch;
